/* LogHomeMart page telemetry.
   Edit tools/telemetry/lhm_track.js in the repo, not the copy on the store.

   TWO destinations, same events:
     GA4     via the store's own gtag from layout/theme.liquid (G-Z35ZWRQB4Q).
             Answers "how many".
     Klaviyo via the _learnq queue from klaviyo.js, loaded by the same snippet
             that loads this file. Answers "who", and holds it against a person
             so a flow can act on it.

   Not everything crosses over. Scroll depth and engaged time go to GA4 only:
   they fire several times a page and would bury a customer's Klaviyo activity
   feed under lines nobody reads. See mirror() for the full mapping.

   Parameter names are the contract in docs/specs/ga4_event_taxonomy.md and must
   match the custom dimensions registered in GA4, character for character.

   PII rule, enforced structurally rather than by care: field values are
   captured by ALLOW-LIST. Numbers, ranges, checkboxes, radios and menus are
   read. Text, email, tel, password and search inputs are never read, so an
   email address cannot reach GA4 or Klaviyo by accident, no matter what gets
   added to a page later. A text field can opt in explicitly with data-lhm-value. */
(function () {
  "use strict";
  if (window.__lhmTrack) return;

  var CFG = window.LHM_TRACK || {};
  var PAGE_KEY = CFG.key || "unknown";
  var PAGE_TYPE = CFG.type || "page";
  var DEBUG = /(?:^|[?&])lhm_debug=1(?:&|$)/.test(location.search);

  /* ---------- transport ---------- */
  function send(name, params) {
    var p = { lhm_page_key: PAGE_KEY, lhm_page_type: PAGE_TYPE };
    if (params) for (var k in params) if (params[k] !== undefined && params[k] !== "") p[k] = params[k];
    if (DEBUG) { p.debug_mode = true; try { console.log("[lhm]", name, p); } catch (e) {} }
    if (typeof window.gtag === "function") window.gtag("event", name, p);
    else if (window.dataLayer) window.dataLayer.push(assign({ event: name }, p));
    mirror(name, p);
  }
  function assign(a, b) { for (var k in b) a[k] = b[k]; return a; }

  /* ---------- Klaviyo mirror ----------
     SCROLL IS DELIBERATELY NOT MIRRORED. It fires four times per page, which
     would roughly quintuple volume, and it fills a customer's activity feed with
     "scrolled 50%" lines that push the useful entries out of view. Scroll stays
     in GA4 where it is an aggregate rather than a biography. Same for engaged
     time. (Juan's call, Aug 2 2026.)

     Names here are what a human reads in Klaviyo's UI. Changing one creates a
     NEW metric and orphans the history under the old name, so treat them as
     fixed once live. */
  var KLAVIYO_ON = true;

  function klq() { return window._learnq || (window._learnq = []); }

  function klaviyoName(name, p) {
    switch (name) {
      case "lhm_link_click":
        if (p.lhm_link_kind === "product") return "Clicked Product";
        if (p.lhm_link_kind === "collection") return "Clicked Collection";
        if (p.lhm_link_kind === "external") return null;
        return "Clicked Link";
      case "lhm_button_click": return "Clicked Button";
      case "lhm_widget_action": return "Used Calculator";
      case "lhm_field_change": return PAGE_TYPE === "calculator" ? "Used Calculator" : null;
      case "lhm_calc_complete": return "Completed Calculator";
      default: return null;
    }
  }

  /* lhm_ names are the GA4 contract. Klaviyo shows property names raw in its
     segment builder, so they get readable ones here. */
  var PROP_NAME = {
    lhm_page_key: "Page", lhm_page_type: "Page Type",
    lhm_link_url: "URL", lhm_link_text: "Text", lhm_link_kind: "Kind",
    lhm_link_zone: "Zone", lhm_field: "Field", lhm_field_value: "Value",
    lhm_widget: "Widget", lhm_choice: "Choice"
  };

  function mirror(name, p) {
    if (!KLAVIYO_ON) return;
    var kName = klaviyoName(name, p || {});
    if (!kName) return;
    var out = {};
    for (var k in p) {
      if (k === "debug_mode") continue;
      out[PROP_NAME[k] || k] = p[k];
    }
    try { klq().push(["track", kName, out]); } catch (e) {}
    if (DEBUG) try { console.log("[lhm->klaviyo]", kName, out); } catch (e) {}
  }

  /* The calculator pages already define window.lhmTrack and already reach GA4
     with it. DECORATE it, never replace it. */
  if (typeof window.lhmTrack === "function") {
    var prevTrack = window.lhmTrack;
    window.lhmTrack = function (n, p) {
      var full = assign({ lhm_page_key: PAGE_KEY, lhm_page_type: PAGE_TYPE }, p || {});
      prevTrack(n, full);
      /* Calculators call lhmTrack directly, so their events (lhm_calc_complete
         above all) never pass through send(). Mirror here too or Klaviyo would
         miss exactly the events that show buying intent. */
      mirror(n, full);
    };
  } else {
    window.lhmTrack = send;
  }
  window.__lhmTrack = 1;

  /* ---------- helpers ---------- */
  function trim(s, n) {
    s = (s || "").replace(/\s+/g, " ").trim();
    return s.length > n ? s.slice(0, n) : s;
  }

  function linkKind(href) {
    if (!href) return "none";
    if (href.indexOf("tel:") === 0) return "tel";
    if (href.indexOf("mailto:") === 0) return "mailto";
    if (href.charAt(0) === "#") return "anchor";
    var a = document.createElement("a");
    a.href = href;
    if (a.hostname && a.hostname !== location.hostname) return "external";
    var p = a.pathname || "";
    if (p.indexOf("/products/") === 0) return "product";
    if (p.indexOf("/collections/") === 0) return "collection";
    if (p.indexOf("/blogs/") === 0) return "article";
    if (p.indexOf("/pages/") === 0) return "page";
    if (p.indexOf("/cart") === 0) return "cart";
    return "internal";
  }

  function linkPath(href) {
    if (!href) return "";
    if (href.indexOf("tel:") === 0 || href.indexOf("mailto:") === 0) return href;
    var a = document.createElement("a");
    a.href = href;
    return a.hostname === location.hostname ? (a.pathname + a.search) : a.href;
  }

  function zoneOf(el) {
    var z = el.closest("[data-lhm-zone]");
    if (z) return z.getAttribute("data-lhm-zone");
    if (el.closest(".calc-result, #fr-detail")) return "result";
    if (el.closest(".bom-wrap, .kit-compare, table")) return "table";
    if (el.closest(".cta-box, .trust-call")) return "cta";
    if (el.closest(".calc-form, form")) return "form";
    if (el.closest("nav, header")) return "nav";
    if (el.closest("footer")) return "footer";
    return "body";
  }

  /* ---------- link clicks ---------- */
  document.addEventListener("click", function (e) {
    var a = e.target && e.target.closest ? e.target.closest("a[href]") : null;
    if (!a) return;
    var href = a.getAttribute("href") || "";
    var kind = linkKind(href);
    /* tel: links are deliberately skipped. The theme already fires phone_click
       globally, and a second event for one tap reads as two calls. */
    if (kind === "tel") return;
    send("lhm_link_click", {
      lhm_link_url: trim(linkPath(href), 100),
      lhm_link_text: trim(a.textContent || a.getAttribute("aria-label"), 100),
      lhm_link_kind: kind,
      lhm_link_zone: zoneOf(a)
    });
  }, true);

  /* ---------- button clicks ---------- */
  document.addEventListener("click", function (e) {
    var t = e.target && e.target.closest ? e.target.closest("button, [role=button]") : null;
    if (!t) return;
    send("lhm_button_click", {
      lhm_field: trim(t.id || t.name || t.className, 60),
      lhm_link_text: trim(t.textContent, 60),
      lhm_link_zone: zoneOf(t)
    });
  }, true);

  /* ---------- control changes ----------
     Allow-list only. See the PII note at the top of this file. */
  var READABLE_INPUT = { number: 1, range: 1, checkbox: 1, radio: 1, date: 1 };

  function readable(el) {
    if (el.tagName === "SELECT") return true;
    if (el.tagName !== "INPUT") return false;
    if (el.hasAttribute("data-lhm-value")) return true;
    return !!READABLE_INPUT[(el.type || "").toLowerCase()];
  }

  function valueOf(el) {
    if (el.type === "checkbox" || el.type === "radio") return el.checked ? "on" : "off";
    if (el.tagName === "SELECT") {
      var o = el.options && el.options[el.selectedIndex];
      return trim(o ? (o.value || o.text) : el.value, 60);
    }
    return trim(el.value, 40);
  }

  document.addEventListener("change", function (e) {
    var el = e.target;
    if (!el || !el.tagName) return;
    if (el.tagName !== "SELECT" && el.tagName !== "INPUT" && el.tagName !== "TEXTAREA") return;
    var name = trim(el.id || el.name || el.className, 60);
    if (!readable(el)) {
      send("lhm_field_change", { lhm_field: name, lhm_field_value: "(not captured)" });
      return;
    }
    send("lhm_field_change", { lhm_field: name, lhm_field_value: valueOf(el) });
  }, true);

  /* ---------- widgets ---------- */
  document.addEventListener("click", function (e) {
    var w = e.target && e.target.closest ? e.target.closest("[data-lhm-widget]") : null;
    if (!w) return;
    send("lhm_widget_action", {
      lhm_widget: trim(w.getAttribute("data-lhm-widget"), 60),
      lhm_choice: trim(w.getAttribute("data-lhm-choice") || w.textContent, 60)
    });
  }, true);

  /* ---------- scroll depth (GA4 only) ---------- */
  var marks = [25, 50, 75, 100], hit = {};
  function onScroll() {
    var h = document.documentElement;
    var max = (h.scrollHeight - h.clientHeight);
    if (max <= 0) return;
    var pct = Math.round(((window.pageYOffset || h.scrollTop) / max) * 100);
    for (var i = 0; i < marks.length; i++) {
      if (pct >= marks[i] && !hit[marks[i]]) {
        hit[marks[i]] = 1;
        send("lhm_scroll", { lhm_scroll_pct: marks[i] });
      }
    }
  }
  var st;
  window.addEventListener("scroll", function () {
    clearTimeout(st);
    st = setTimeout(onScroll, 200);
  }, { passive: true });

  /* ---------- engaged time (GA4 only) ---------- */
  var started = Date.now(), engaged = 0, visible = !document.hidden, done = false;
  function stopClock() { if (visible) { engaged += Date.now() - started; visible = false; } }
  function startClock() { if (!visible) { started = Date.now(); visible = true; } }
  document.addEventListener("visibilitychange", function () {
    document.hidden ? stopClock() : startClock();
  });
  function report() {
    if (done) return;
    done = true;
    stopClock();
    var secs = Math.round(engaged / 1000);
    if (secs > 0) send("lhm_page_engagement", { lhm_engaged_seconds: secs });
  }
  window.addEventListener("pagehide", report);
  document.addEventListener("visibilitychange", function () { if (document.hidden) report(); });

  /* ---------- landings ----------
     klaviyo.js records Active on Site by itself, but that is a bare URL with no
     sense of what kind of page it was. This adds the identity the theme already
     worked out, and a proper Viewed Product wherever it supplied product detail,
     which is the event a browse-abandonment flow triggers on. */
  (function landing() {
    if (!KLAVIYO_ON) return;
    var base = {
      Page: PAGE_KEY,
      "Page Type": PAGE_TYPE,
      URL: location.pathname + location.search,
      Title: trim(document.title, 120)
    };
    try { klq().push(["track", "Viewed Page", base]); } catch (e) {}

    var pr = CFG.product;
    if (pr && pr.title) {
      var url = location.origin + location.pathname;
      try {
        klq().push(["track", "Viewed Product", {
          ProductName: pr.title,
          ProductID: String(pr.id || ""),
          SKU: pr.sku || "",
          Categories: pr.type ? [pr.type] : [],
          Brand: pr.vendor || "",
          ImageURL: pr.image || "",
          URL: url,
          Price: pr.price,
          $value: pr.price
        }]);
        /* Klaviyo's "recently viewed" email block reads this queue, not the
           event above. Both are needed; they feed different things. */
        klq().push(["trackViewedItem", {
          Title: pr.title,
          ItemId: String(pr.id || ""),
          Categories: pr.type ? [pr.type] : [],
          ImageUrl: pr.image || "",
          Url: url,
          Metadata: { Brand: pr.vendor || "", Price: pr.price }
        }]);
      } catch (e) {}
    }
    if (DEBUG) try { console.log("[lhm->klaviyo] Viewed Page", base, pr || "(no product)"); } catch (e) {}
  })();

  if (DEBUG) try { console.log("[lhm] telemetry ready", PAGE_KEY, PAGE_TYPE); } catch (e) {}
})();